import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createWsClient, WsClient, ConnectionState } from 'util/ws-client'
import type { ServerToClientMessage } from '@all-for-one/shared-types'
import { ConnectionBanner } from 'components/connection-banner'
import { useGetLessonQuery } from 'util/rtk-api'
import { LoadingModal } from 'components/loading-model'
import { GenericOverlay } from 'components/lesson-builder/components/generic-overlay'
import { PresentationSlide } from 'components/lesson-builder/presentation-slide'
import { usePresenterSlides } from 'components/lesson-slides/usePresenterSlides'
import { useHighContrast, HighContrastToggle } from 'components/high-contrast-toggle'
import { useSpeech, ReadAloudButton } from 'components/speech'
import { SlideAnnotation, useSlideAnnotation } from 'components/slide-annotation'
import { QuestionInProgressPage } from 'components/presentation-status-page'
import { NextSlideButton, PreviousSlideButton, DoneButton } from 'components/blue-button'
import styles from 'css/pages/present-lesson.module.css'
import overlayStyles from 'css/components/generic-overlay.module.css'
import { verifyLogin } from 'util/cache'

export const PresentLessonPage: React.FC = () => {
	const { classId, lessonId } = useParams()
	const [code, setCode] = useState<string | null>(null)
	const [ttlSeconds, setTtlSeconds] = useState<number | null>(null)
	const [showLoading, setShowLoading] = useState(false)
	const [showGradingComplete, setShowGradingComplete] = useState(false)
	const [gradingError, setGradingError] = useState<string | null>(null)
	const [gradingProgress, setGradingProgress] = useState<{ graded: number, total: number } | null>(null)
	const [connectionState, setConnectionState] = useState<ConnectionState>({ status: 'connecting' })
	const { data: lesson, isError: lessonLoadFailed, refetch: refetchLesson } = useGetLessonQuery({ lessonId: lessonId as string })

	useEffect(() => {
		verifyLogin(navigate)
	}, [])

	const {
		lessonPdf,
		slides,
		currentSlide,
		videoLinksByPage,
		slideTextByPage,
		setCurrentSlide,
	} = usePresenterSlides({ lessonDetails: lesson || null })

	const { highContrast, toggleHighContrast } = useHighContrast()
	const speech = useSpeech()
	const { annotationOpen, toggleAnnotation } = useSlideAnnotation()

	/* Stop read-aloud when the teacher moves on. Otherwise the previous slide
	   keeps being read over the new one, in front of a class. */
	const stopSpeech = speech.stop
	useEffect(() => { stopSpeech() }, [currentSlide, stopSpeech])

	const navigate = useNavigate()

	// Measure the content area for slide scaling
	const contentRef = useRef<HTMLDivElement>(null)
	const [containerSize, setContainerSize] = useState({ width: 960, height: 700 })

	useEffect(() => {
		const measure = () => {
			if (!contentRef.current) return
			setContainerSize({
				width: contentRef.current.clientWidth,
				height: contentRef.current.clientHeight
			})
		}
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
	}, [])

	const wsRef = useRef<WsClient>(createWsClient())
	const ws = wsRef.current
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const hasStartedSessionRef = useRef(false)

	// Track last slide index we broadcasted to avoid duplicate sends
	const lastBroadcastSlideRef = useRef<number | null>(null)
	// Track previous slide index to detect leaving a question slide
	const previousSlideIndexRef = useRef<number | null>(null)

	useEffect(() => {
		const handleMessage = (msg: ServerToClientMessage) => {
			switch (msg.type) {
				case 'system':
					if (msg.event === 'authenticated') {
						setIsAuthenticated(true)
					}
					break
				case 'session:code':
					setCode(msg.code)
					setTtlSeconds(msg.ttlSeconds)
					break
				case 'grading:progress':
					setGradingProgress({ graded: (msg as any).graded, total: (msg as any).total })
					break
				case 'grading:complete':
					// Grading is actually complete now
					console.log('Grading complete!')
					setShowLoading(false)
					setGradingProgress(null)
					setShowGradingComplete(true)
					break
				case 'error':
					// err-2/WS-4: GRADING_FAILED (and any other server error frame)
					// used to be silently dropped, leaving the grading modal
					// spinning forever.
					console.error(`WS error frame: ${(msg as any).code} - ${(msg as any).message}`)
					if ((msg as any).code === 'GRADING_FAILED') {
						setShowLoading(false)
						setGradingProgress(null)
						setGradingError('Some responses could not be graded right now. The system will retry automatically — check the results page in a few minutes.')
					}
					break
			}
		}

		ws.onMessage(handleMessage)
		return () => {
			ws.offMessage(handleMessage)
		}
	}, [])

	// Connect once on mount, cleanup on unmount
	useEffect(() => {
		ws.connect()
		return () => {
			ws.close()
		}
	}, [])

	// Listen for connection state changes
	useEffect(() => {
		const handleConnectionStateChange = (state: ConnectionState) => {
			setConnectionState(state)

			// When reconnected, trigger session restart
			// Server will reuse existing session code for same classroom/lesson
			// so students stay connected with same code
			if (state.status === 'connected' && hasStartedSessionRef.current) {
				// Reset both flags to trigger session restart
				setIsAuthenticated(false)
				hasStartedSessionRef.current = false
			}
		}

		ws.onConnectionStateChange(handleConnectionStateChange)

		return () => {
			ws.offConnectionStateChange(handleConnectionStateChange)
		}
	}, [])

	// Start session once both authenticated and lesson name is available
	useEffect(() => {
		if (!isAuthenticated || hasStartedSessionRef.current) return
		if (classId && lessonId && lesson?.name) {
			ws.startSession(classId, lessonId, lesson.name, lesson?.eduqueryLessonId || '')
			hasStartedSessionRef.current = true
		}
	}, [isAuthenticated, classId, lessonId, lesson?.name, lesson?.eduqueryLessonId])

	// Allow re-start when navigating to a different class/lesson
	useEffect(() => {
		hasStartedSessionRef.current = false
	}, [classId, lessonId])

	const onSessionEnd = () => {
		ws.send({ type: 'session:end' })
		setShowLoading(true)
		// Grading completion will be triggered by 'grading:complete' WebSocket message
	}

	// When the current slide is a question slide, broadcast the questions to students
	useEffect(() => {
		if (!code) return // ensure session started/role set
		const slide = slides[currentSlide]
		if (!slide || slide.type !== 'question') return
		// if (lastBroadcastSlideRef.current === currentSlide) return
		try {
			ws.send({ type: 'question:broadcast', payload: slide.questionsByDifficulty, learningStage: String(slide.learningStage || '') })
			// lastBroadcastSlideRef.current = currentSlide
		} catch {
			// no-op: if ws not ready, we simply skip broadcasting
		}
	}, [code, currentSlide, slides])

	// When navigating away from a question slide to a non-question slide, notify students
	useEffect(() => {
		if (!code) {
			previousSlideIndexRef.current = currentSlide
			return
		}
		const prevIndex = previousSlideIndexRef.current
		const prevSlide = prevIndex != null ? slides[prevIndex] : undefined
		const nextSlide = slides[currentSlide]
		if (prevSlide && prevSlide.type === 'question' && nextSlide && nextSlide.type !== 'question') {
			try {
				// Flatten previous questions for payload to satisfy type; server ignores payload
				const payload = (prevSlide.questionsByDifficulty || []).flatMap(g => g.questions)
				ws.send({ type: 'question:end:broadcast', payload })
			} catch { }
		}
		previousSlideIndexRef.current = currentSlide
	}, [code, currentSlide, slides])

	const body = (
		<>
			<div className={styles.center}>
				<div className={styles.gradingCompleteMessageBig}>
					All Set!
				</div>
				<div className={styles.gradingCompleteMessage}>
					{`Grading for this lesson is complete: ${lesson?.name}

					Need to make a change? You can still review and adjust any response.`}
				</div>
				<div className={overlayStyles.actionRow}>
					<div className={overlayStyles.actionButton} onClick={() => { navigate(`/dashboard`) }}>
						{'Later'}
					</div>

					<div className={overlayStyles.actionButton} onClick={() => { navigate(`/classrooms/${classId}/lessons/${lessonId}`) }}>
						{'Review Answers'}
					</div>
				</div>
			</div>
		</>
	)

	// err-5: the lesson fetch failing used to leave this page hanging blank
	if (lessonLoadFailed) {
		return (
			<div className={styles.presentLessonContainer}>
				<div className={styles.center} style={{ textAlign: 'center', padding: '48px' }}>
					<h2>We couldn't load this lesson</h2>
					<p>Check your connection and try again.</p>
					<DoneButton onClick={() => refetchLesson()} />
				</div>
			</div>
		)
	}

	return (
		<div className={styles.presentLessonContainer}>
			{/* Connection status banner */}
			{connectionState.status === 'reconnecting' && (
				<ConnectionBanner
					reconnectAttempt={connectionState.reconnectAttempt}
					maxAttempts={10}
				/>
			)}
			{connectionState.status === 'failed' && (
				<div style={{ background: '#b23434', color: '#fff', padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}>
					Connection lost and could not be restored — check your internet and refresh this page. Students keep their answers.
				</div>
			)}

			<div className={styles.content} ref={contentRef}>
				{slides[currentSlide]?.type === 'presentation'
					? (
						<>
							<PresentationSlide
								lessonPdf={lessonPdf}
								pageNumber={(slides[currentSlide].index as number) + 1}
								isCurrent={currentSlide === slides[currentSlide].index}
								containerWidth={containerSize.width}
								containerHeight={containerSize.height}
								videoLinks={videoLinksByPage[(slides[currentSlide].index as number) + 1] ?? []}
							/>
							<SlideAnnotation
								slideKey={String(slides[currentSlide].index)}
								active={annotationOpen}
							/>
						</>
					)
					: (
						<QuestionInProgressPage learningStage={slides[currentSlide]?.learningStage} />
					)
				}
			</div>
			<div className={styles.bottomNav}>
				<div className={styles.navLeft}>
					{currentSlide > 0 && (
						<PreviousSlideButton onClick={() => setCurrentSlide(currentSlide - 1)} />
					)}
					<HighContrastToggle on={highContrast} onToggle={toggleHighContrast} />
					{slides[currentSlide]?.type === 'presentation' && (
						<button
							type="button"
							className={`annotation-toggle ${annotationOpen ? 'active' : ''}`}
							onClick={toggleAnnotation}
							aria-pressed={annotationOpen}
						>Markup</button>
					)}
					{/* Only offered when the slide actually has extracted text. Our board
					    shows the slide as an image, so a slide EduQuery got nothing from
					    has nothing to read -- and a button that silently does nothing is
					    worse than no button. */}
					{slides[currentSlide]?.type === 'presentation'
						&& !!slideTextByPage[(slides[currentSlide].index as number) + 1] && (
						<ReadAloudButton
							speech={speech}
							label="Read slide"
							getText={() => slideTextByPage[(slides[currentSlide].index as number) + 1] || ''}
						/>
					)}
				</div>
				<div className={styles.navCenter}>Join Code<span className={styles.navCenterCode}>{code}</span></div>
				<div className={styles.navRight}>
					{currentSlide === slides.length - 1 ? (
						<DoneButton onClick={onSessionEnd} />
					) : (
						<NextSlideButton onClick={() => setCurrentSlide(currentSlide + 1)} />
					)}
				</div>
			</div>


			{/* Overlays */}
			{showLoading && (
				<LoadingModal>
					<div>Evaluating student responses for:</div>
					<div style={{
						wordBreak: 'break-word',
						maxWidth: '40ch',
						fontWeight: 500
					}}>
						{lesson?.name || ''}
					</div>
					{gradingProgress && (
						<div style={{ marginTop: '8px' }}>
							{`Graded ${gradingProgress.graded} of ${gradingProgress.total} students…`}
						</div>
					)}
				</LoadingModal>
			)}
			{gradingError && (
				<GenericOverlay
					body={(
						<div className={styles.center}>
							<div className={styles.gradingCompleteMessageBig}>Grading didn't finish</div>
							<div className={styles.gradingCompleteMessage}>{gradingError}</div>
							<div className={overlayStyles.actionRow}>
								<div className={overlayStyles.actionButton} onClick={() => { setGradingError(null); navigate('/dashboard') }}>
									{'OK'}
								</div>
							</div>
						</div>
					)}
				/>
			)}
			{showGradingComplete && (
				<GenericOverlay
					body={body}
				/>
			)}
		</div>
	)
}


